export interface ShopifyTriggerVariableField {
  path: string;
  label: string;
}

export interface ShopifyTriggerEvent {
  id: string;
  label: string;
  description: string;
  triggerType: string;
  legacyNodeType: string;
  variableFields: ShopifyTriggerVariableField[];
}

const ORDER_FIELDS: ShopifyTriggerVariableField[] = [
  { path: 'orderDetails.name', label: 'Order Name' },
  { path: 'orderDetails.id', label: 'Order ID' },
  { path: 'orderDetails.total_price', label: 'Order Price' },
  { path: 'orderDetails.currency', label: 'Order Currency' },
  { path: 'orderDetails.status', label: 'Order Status' },
  { path: 'orderDetails.financial_status', label: 'Payment Status' },
  { path: 'orderDetails.fulfillment_status', label: 'Fulfillment Status' },
  { path: 'orderDetails.note', label: 'Order Note' },
  { path: 'orderDetails.order_status_url', label: 'Order Link' },
  { path: 'orderDetails.line_items[*].name', label: 'Order Products List' },
];

const ORDER_CUSTOMER_FIELDS: ShopifyTriggerVariableField[] = [
  { path: 'orderDetails.customer.first_name', label: 'Customer First Name' },
  { path: 'orderDetails.customer.last_name', label: 'Customer Last Name' },
  { path: 'orderDetails.customer.default_address.name', label: 'Customer Full Name' },
  { path: 'orderDetails.email', label: 'Customer Email' },
  { path: 'orderDetails.customer.phone', label: 'Customer Phone' },
];

const ORDER_SUMMARY_FIELDS: ShopifyTriggerVariableField[] = [
  { path: 'orderDetails.name', label: 'Order Name' },
  { path: 'orderDetails.total_price', label: 'Order Price' },
];

const BASE_FIELD: ShopifyTriggerVariableField = { path: '', label: '(Formatted)' };

const ORDER_EVENT_FIELDS: ShopifyTriggerVariableField[] = [
  BASE_FIELD,
  ...ORDER_FIELDS,
  ...ORDER_CUSTOMER_FIELDS,
];

const FULFILLMENT_EVENT_FIELDS: ShopifyTriggerVariableField[] = [
  BASE_FIELD,
  { path: 'fulfillmentDetails.tracking_company', label: 'Tracking Company' },
  { path: 'fulfillmentDetails.tracking_number', label: 'Tracking Number' },
  { path: 'fulfillmentDetails.tracking_url', label: 'Tracking URL' },
  { path: 'fulfillmentDetails.status', label: 'Fulfillment Status' },
  ...ORDER_SUMMARY_FIELDS,
  ...ORDER_CUSTOMER_FIELDS,
];

export const SHOPIFY_TRIGGER_EVENTS: ShopifyTriggerEvent[] = [
  {
    id: 'order_created',
    label: 'Order Created',
    description: 'Runs when a new order is placed in your connected store.',
    triggerType: 'order.placed',
    legacyNodeType: 'shopify_order_created_trigger',
    variableFields: ORDER_EVENT_FIELDS,
  },
  {
    id: 'order_updated',
    label: 'Order Updated',
    description: 'Runs when an existing order is updated.',
    triggerType: 'order.updated',
    legacyNodeType: 'shopify_order_updated_trigger',
    variableFields: ORDER_EVENT_FIELDS,
  },
  {
    id: 'order_paid',
    label: 'Order Paid',
    description: 'Runs when payment for an order is captured.',
    triggerType: 'order.paid',
    legacyNodeType: 'shopify_order_paid_trigger',
    variableFields: ORDER_EVENT_FIELDS,
  },
  {
    id: 'order_shipped',
    label: 'Order Shipped',
    description: 'Runs when an order ships and is in transit.',
    triggerType: 'order.shipped',
    legacyNodeType: 'shopify_order_shipped_trigger',
    variableFields: FULFILLMENT_EVENT_FIELDS,
  },
  {
    id: 'order_out_for_delivery',
    label: 'Out For Delivery',
    description: 'Runs when an order is out for delivery.',
    triggerType: 'order.out_for_delivery',
    legacyNodeType: 'shopify_order_out_for_delivery_trigger',
    variableFields: FULFILLMENT_EVENT_FIELDS,
  },
  {
    id: 'order_delivered',
    label: 'Order Delivered',
    description: 'Runs when an order is marked delivered.',
    triggerType: 'order.delivered',
    legacyNodeType: 'shopify_order_delivered_trigger',
    variableFields: FULFILLMENT_EVENT_FIELDS,
  },
  {
    id: 'order_cancelled',
    label: 'Order Cancelled',
    description: 'Runs when an order is cancelled.',
    triggerType: 'order.cancelled',
    legacyNodeType: 'shopify_order_cancelled_trigger',
    variableFields: [
      ...ORDER_EVENT_FIELDS,
      { path: 'cancelReason', label: 'Cancel Reason' },
    ],
  },
  {
    id: 'refund_created',
    label: 'Refund Created',
    description: 'Runs when a refund is created for an order.',
    triggerType: 'refund.created',
    legacyNodeType: 'shopify_refund_created_trigger',
    variableFields: [
      BASE_FIELD,
      { path: 'refundDetails.order_id', label: 'Order ID' },
      { path: 'refundDetails.note', label: 'Refund Note' },
      ...ORDER_SUMMARY_FIELDS,
      ...ORDER_CUSTOMER_FIELDS,
    ],
  },
  {
    id: 'abandoned_cart',
    label: 'Abandoned Cart',
    description: 'Runs when a checkout is abandoned or updated.',
    triggerType: 'checkout.updated',
    legacyNodeType: 'shopify_abandoned_cart_trigger',
    variableFields: [
      BASE_FIELD,
      { path: 'checkoutDetails.abandoned_checkout_url', label: 'Recovery Link' },
      { path: 'checkoutDetails.total_price', label: 'Cart Total' },
      { path: 'checkoutDetails.customer.first_name', label: 'Customer First Name' },
      { path: 'checkoutDetails.customer.last_name', label: 'Customer Last Name' },
      { path: 'checkoutDetails.email', label: 'Customer Email' },
      { path: 'checkoutDetails.customer.phone', label: 'Customer Phone' },
    ],
  },
  {
    id: 'customer_created',
    label: 'New Subscriber',
    description: 'Runs when a new customer is created in your store.',
    triggerType: 'customer.created',
    legacyNodeType: 'shopify_customer_created_trigger',
    variableFields: [
      BASE_FIELD,
      { path: 'customerDetails.first_name', label: 'Customer First Name' },
      { path: 'customerDetails.last_name', label: 'Customer Last Name' },
      { path: 'customerDetails.email', label: 'Customer Email' },
      { path: 'customerDetails.phone', label: 'Customer Phone' },
    ],
  },
];

export const SHOPIFY_EVENT_TRIGGER_TYPE = 'shopify_event_trigger';

export const DEFAULT_SHOPIFY_EVENT_ID = 'order_created';

export function getShopifyEvent(eventId?: string | null): ShopifyTriggerEvent {
  const found = SHOPIFY_TRIGGER_EVENTS.find((e) => e.id === eventId);
  return found ?? SHOPIFY_TRIGGER_EVENTS.find((e) => e.id === DEFAULT_SHOPIFY_EVENT_ID)!;
}

export function shopifyTriggerTypeFor(eventId?: string | null): string {
  return getShopifyEvent(eventId).triggerType;
}

export function eventIdForLegacyNodeType(nodeType: string): string | null {
  return SHOPIFY_TRIGGER_EVENTS.find((e) => e.legacyNodeType === nodeType)?.id ?? null;
}

export function shopifyEventIdFromNodeData(
  data?: Record<string, unknown> | null,
): string | undefined {
  const nested = data?.config as Record<string, unknown> | undefined;
  return (data?.shopifyEvent ?? nested?.shopifyEvent) as string | undefined;
}

export function shopifyVariableFieldsFor(
  eventIdOrLegacyType?: string | null,
): ShopifyTriggerVariableField[] {
  const legacyMatched = eventIdOrLegacyType
    ? eventIdForLegacyNodeType(eventIdOrLegacyType)
    : null;
  return getShopifyEvent(legacyMatched ?? eventIdOrLegacyType).variableFields;
}

export const LEGACY_SHOPIFY_NODE_TYPES = SHOPIFY_TRIGGER_EVENTS.map(
  (e) => e.legacyNodeType,
);
